import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { getApiError } from '../../lib/utils';
import AdminLayout from '../../components/layouts/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { UserCog, Plus, Mail, Shield, Trash2, User } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StaffManagement = () => {
  const { user } = useAuth();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [newStaff, setNewStaff] = useState({
    full_name: '',
    email: '',
    phone: '',
    password: '',
    role: 'admin'
  });

  useEffect(() => {
    fetchStaff();
  }, []);

  const fetchStaff = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/staff`);
      setStaff(response.data.staff);
    } catch (err) {
      console.error('Error fetching staff:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateStaff = async () => {
    if (!newStaff.full_name || !newStaff.email || !newStaff.password) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/auth/register/staff`, newStaff);
      toast.success('Staff member created');
      setDialogOpen(false);
      setNewStaff({ full_name: '', email: '', phone: '', password: '', role: 'admin' });
      fetchStaff();
    } catch (err) {
      toast.error(getApiError(err, 'Failed to create staff member'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteStaff = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this staff member?')) return;

    try {
      await axios.delete(`${API_URL}/api/staff/${userId}`);
      toast.success('Staff member deleted');
      fetchStaff();
    } catch (err) {
      toast.error(getApiError(err, 'Failed to delete staff member'));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  if (user?.role !== 'super_admin') {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <Shield className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">Only Admins can access staff management</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="animate-fade-in" data-testid="staff-management-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="page-title">Staff Management</h1>
            <p className="text-slate-600 mt-1">Manage internal staff accounts and permissions</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="add-staff-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Staff Member
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Add New Staff Member</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label>Full Name *</Label>
                  <Input
                    value={newStaff.full_name}
                    onChange={(e) => setNewStaff({ ...newStaff, full_name: e.target.value })}
                    placeholder="John Smith"
                    data-testid="staff-name-input"
                  />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={newStaff.email}
                    onChange={(e) => setNewStaff({ ...newStaff, email: e.target.value })}
                    placeholder="john@cargodash.co.za"
                    data-testid="staff-email-input"
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={newStaff.phone}
                    onChange={(e) => setNewStaff({ ...newStaff, phone: e.target.value })}
                    placeholder="+27 82 555 1234"
                  />
                </div>
                <div>
                  <Label>Password *</Label>
                  <Input
                    type="password"
                    value={newStaff.password}
                    onChange={(e) => setNewStaff({ ...newStaff, password: e.target.value })}
                    placeholder="Create a password"
                    data-testid="staff-password-input"
                  />
                </div>
                <div>
                  <Label>Role</Label>
                  <Select value={newStaff.role} onValueChange={(v) => setNewStaff({ ...newStaff, role: v })}>
                    <SelectTrigger data-testid="staff-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin / Operations</SelectItem>
                      <SelectItem value="super_admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={handleCreateStaff} disabled={submitting} className="w-full btn-primary">
                  {submitting ? 'Creating...' : 'Create Staff Member'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Staff List */}
        <Card className="card-elevated">
          <CardHeader className="border-b border-slate-100 pb-4">
            <div className="flex items-center space-x-3">
              <div className="h-10 w-10 rounded-lg bg-purple-50 flex items-center justify-center">
                <UserCog className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <CardTitle className="card-title">Staff Members</CardTitle>
                <p className="text-sm text-slate-500">{staff.length} team members</p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-700"></div>
              </div>
            ) : staff.length > 0 ? (
              <div className="divide-y divide-slate-100">
                {staff.map((member) => (
                  <div key={member.user_id} className="p-4 sm:p-6 flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="h-12 w-12 rounded-full bg-slate-100 flex items-center justify-center">
                        <User className="h-6 w-6 text-slate-500" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{member.full_name}</p>
                        <div className="flex items-center text-sm text-slate-500 mt-0.5">
                          <Mail className="h-3 w-3 mr-1" />
                          {member.email}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <span className="text-sm text-slate-500">
                        Joined {formatDate(member.created_at)}
                      </span>
                      {member.user_id !== user?.user_id && (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => handleDeleteStaff(member.user_id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-slate-500">
                No staff members found
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default StaffManagement;
